/**
 * Decision client (2026-08-20 decision doctrine — the frontend twin of the decision
 * transport, THIN by design).
 *
 * The client never resolves a model, ladder, or effort for a decision. It posts
 * `{ mode: 'decision', route, messages, maxTokens }` to the anthropicProxy function and
 * the SERVER resolves everything from the SSOT registry (rollout-staged there:
 * ops/decisionRollout 'live' rides the full-ladder/tier-effort contract transport;
 * 'legacy'/'shadow' serve the pre-doctrine rung-0 body). This is what makes a tier swap
 * reach every client without a frontend deploy — the stale-CDN-bundle class behind the
 * 2026-07-02 thread-label request storm.
 *
 * Callers keep their own legacy transport as the fallback for one release (deploy skew:
 * an old function build has no decision mode) — any throw here means "use your legacy
 * path", never "give up".
 */

import { DECISION_ROUTES } from '../config/modelTiers.generated';

export interface DecisionRequest {
  /** SSOT decision route — the ONLY routing input. */
  route: string;
  messages: Array<{ role: string; content: string }>;
  maxTokens: number;
  system?: string;
  sessionId?: string;
  accountId?: string;
  proxyUrl: string;
  signal?: AbortSignal;
}

export interface DecisionResponse {
  text: string;
  model: string;
  /** Rollout state the server reports it served under (absent on legacy builds). */
  rollout?: string;
  /** ACTUAL effort the served window rode (live rollout only) — receipts
   *  render this instead of the display-time SSOT inference. */
  effort?: string;
  /** Upstream usage when the server reports it (live rollout; legacy bodies
   *  carry the raw OpenRouter usage object instead, parsed here too). */
  usage?: { inputTokens?: number; outputTokens?: number; costUsd?: number };
}

export async function requestDecision(input: DecisionRequest): Promise<DecisionResponse> {
  if (!DECISION_ROUTES.includes(input.route)) {
    throw new Error(`requestDecision: unknown decision route '${input.route}'`);
  }
  const response = await fetch(input.proxyUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Prest0n-Task': input.route,
    },
    body: JSON.stringify({
      mode: 'decision',
      route: input.route,
      messages: input.messages,
      maxTokens: input.maxTokens,
      ...(input.system ? { system: input.system } : {}),
      ...(input.sessionId ? { sessionId: input.sessionId } : {}),
      ...(input.accountId ? { accountId: input.accountId } : {}),
    }),
    signal: input.signal,
  });
  if (!response.ok) {
    throw new Error(`requestDecision: proxy returned ${response.status}`);
  }
  const body = (await response.json()) as Record<string, unknown>;
  let text = '';
  const choices = body.choices;
  if (Array.isArray(choices) && choices.length > 0) {
    const msg = (choices[0] as Record<string, unknown>)?.message as Record<string, unknown> | undefined;
    if (msg && typeof msg.content === 'string') text = msg.content;
  }
  if (!text && typeof body.text === 'string') text = body.text;
  // Usage: the live serve reports {prompt_tokens, completion_tokens, cost};
  // legacy bodies pass the raw OpenRouter usage through — same field names.
  const rawUsage = body.usage as Record<string, unknown> | undefined;
  const usage = rawUsage && typeof rawUsage === 'object'
    ? {
        inputTokens: typeof rawUsage.prompt_tokens === 'number' ? rawUsage.prompt_tokens : undefined,
        outputTokens: typeof rawUsage.completion_tokens === 'number' ? rawUsage.completion_tokens : undefined,
        costUsd: typeof rawUsage.cost === 'number' ? rawUsage.cost : undefined,
      }
    : undefined;
  return {
    text,
    model: typeof body.model === 'string' ? body.model : '',
    rollout: typeof body.decisionRollout === 'string' ? body.decisionRollout : undefined,
    effort: typeof body.effort === 'string' && body.effort ? body.effort : undefined,
    usage,
  };
}
